/**
 * What a post in flight carries that the mutation cache has no slot for
 * (#1069 Story 6.6, D2).
 *
 * Since Story 6.6 a pending row is a JOIN of two kinds of truth. WHICH posts
 * are in flight is server state, and the mutation cache records it per
 * mutation (`variables`, `status`, `submittedAt`), which is what
 * `useMutationState` reads. HOW FAR ALONG each one is -- `percent`, `slow` --
 * is UI state that changes WHILE the mutation runs, and the library has
 * nowhere to put it: the mutation function's context is
 * `{ client, meta, mutationKey }` and its variables are captured at `mutate()`
 * and never change (`mutation.js:56-64`). The abort handle and the staged
 * bytes have the same shape of problem. This module is that overlay, and
 * nothing else is.
 *
 * ⚠️ IT IS KEYED ON THE `QueryClient`, WHICH IS THE LIFETIME THE MUTATION HAS.
 * The obvious home -- a `useState` map and a `useRef` of handles inside
 * `useCorrespondence` -- has the HOOK's lifetime, and on the docked
 * presentation those differ: `CorrespondenceDock.tsx:342-345` unmounts the
 * panel after the close animation while the upload keeps going, so on reopen
 * the row would come back from the cache with no `percent` (a bar stuck at 0%)
 * and a Cancel wired to `undefined?.()`. Keying on the client gives the
 * overlay exactly the mutation cache's own lifetime: the app's singleton
 * client for the session, and the harness's fresh-client-per-render for a
 * test. A `WeakMap`, so a discarded client takes its entries with it.
 *
 * ⚠️ AND NOT THE QUERY CACHE. `setQueryData` per upload would put UI state and
 * a FUNCTION (the abort handle) into the query cache, where the teardown
 * guard, `gcTime` and the "no `new QueryClient(`" rule all have opinions, and
 * which the library's own maintainers say is not a client-state store.
 *
 * Notifications are a synchronous listener loop, deliberately NOT through
 * `notifyManager`: progress arrives from XHR events outside React exactly as
 * the old `setState` did, and a second macrotask hop would only widen the
 * flush class the tests have to account for.
 *
 * ⚠️ AN ENTRY'S LIFETIME IS BOUNDED BY ITS MUTATION'S PRESENCE IN THE CACHE
 * (#1069 Story 6.7, D6). `forget` is reachable from the post mutation's
 * `onSuccess`/`onError`, and Story 6.7's transport guarantees those run, so
 * a post that stalls settles into `forget` like any other. What that does not
 * cover is the mutation being REMOVED from under the upload:
 * `queryClient.clear()` -- every 401 site in `src/lib/queryClient.ts` and
 * `AuthContext.tsx` -- drops the mutation and emits `removed` for it, the row
 * goes at once (the derivation reads the cache), and before this story the
 * XHR kept uploading into a cleared cache while the entry sat here for the
 * life of the client. So `storeFor` subscribes to the client's mutation cache
 * ONCE when it mints the store, and a `removed` for one of the POST's own
 * mutations (matched by key) whose `variables.localId` it still holds ABORTS
 * the upload and FORGETS the entry. Never unsubscribed:
 * the store has the client's lifetime by construction and a `WeakMap` entry
 * dies with it. Not a `MutationCache` config option, because `queryClient.ts`
 * is app-wide and the test harness mints its own clients: a subscription from
 * here reaches both. The aborted upload rejects `aborted: true`, so the
 * mutation's own `onError` (which still runs after removal, `mutation.js`)
 * takes its cancel branch and the wrapper resolves `'cancelled'`.
 */
import { hashKey, type QueryClient } from '@tanstack/react-query';

import { correspondenceMutationKeys } from './correspondenceQueries';

import type { OutgoingMessageFile } from './providers/provider.types';

/**
 * The post's mutation key, hashed once. The cache subscription below answers
 * only to removals of the post's OWN mutations: the store is keyed on the
 * app-wide client, and another feature's mutation that happened to carry a
 * `localId` must not reach a correspondence upload (review of Story 6.7).
 * `correspondenceQueries` imports only a TYPE from here, so there is no
 * runtime cycle.
 */
const POST_KEY_HASH = hashKey(correspondenceMutationKeys.post());

/** One in-flight post's UI state, its handle and its bytes, under its `localId`. */
export interface UploadOverlay {
  /** `null` means unmeasurable, exactly as `PendingMessage.percent` does. */
  percent: number | null;
  /** AC 10: the row admits it is taking a while. */
  slow: boolean;
  /**
   * Abort the upload (AC 11, FR18). Registered by the mutation function BEFORE
   * the first byte goes out, so a cancel pressed in the first moments finds it.
   */
  abort?: () => void;
  /**
   * The staged bytes, here rather than in `PostMessageVariables` because the
   * library retains variables for the life of the observer (Story 6.5 code
   * review, decision 1; that interface carries the measurement). Deleted by
   * the same `forget` as everything else, so the two cannot drift apart.
   */
  files?: OutgoingMessageFile[];
}

interface Store {
  /**
   * Replaced, never mutated in place: `useSyncExternalStore` compares the
   * snapshot by identity and would loop forever on a map that mutated.
   */
  entries: ReadonlyMap<string, UploadOverlay>;
  listeners: Set<() => void>;
}

const EMPTY: ReadonlyMap<string, UploadOverlay> = new Map();

const stores = new WeakMap<QueryClient, Store>();

const storeFor = (client: QueryClient): Store => {
  const existing = stores.get(client);
  if (existing) return existing;
  const fresh: Store = { entries: EMPTY, listeners: new Set() };
  stores.set(client, fresh);
  // See the header: the entry cannot outlive its mutation in the cache.
  client.getMutationCache().subscribe(event => {
    if (event.type !== 'removed') return;
    if (hashKey(event.mutation.options.mutationKey ?? []) !== POST_KEY_HASH) {
      return;
    }
    const localId = (
      event.mutation.state.variables as { localId?: unknown } | undefined
    )?.localId;
    if (typeof localId !== 'string') return;
    const entry = fresh.entries.get(localId);
    // Settled and forgotten already (gc), or a mutation that never had an
    // entry: not a write, so the readers are not told.
    if (!entry) return;
    entry.abort?.();
    forget(client, localId);
  });
  return fresh;
};

/**
 * Apply one change and tell the readers, or do nothing at all. A write that
 * would not change anything does NOT notify: a `forget` of an id that has
 * already gone is an ordinary occurrence (every exit path calls it) and must
 * not re-render the panel.
 */
const write = (
  client: QueryClient,
  change: (entries: Map<string, UploadOverlay>) => boolean,
): void => {
  const store = storeFor(client);
  const next = new Map(store.entries);
  if (!change(next)) return;
  store.entries = next;
  store.listeners.forEach(listener => listener());
};

/** Start tracking one post, before its mutation is issued (C10). */
export const seed = (
  client: QueryClient,
  localId: string,
  entry: UploadOverlay,
): void => {
  write(client, entries => {
    entries.set(localId, entry);
    return true;
  });
};

/** Move one in-flight post's bar, flag or handle. A no-op once it has gone. */
export const patch = (
  client: QueryClient,
  localId: string,
  fields: Partial<UploadOverlay>,
): void => {
  write(client, entries => {
    const current = entries.get(localId);
    if (!current) return false;
    entries.set(localId, { ...current, ...fields });
    return true;
  });
};

/**
 * Drop everything one finished post was holding: the bar, the handle and the
 * bytes together. ONE function so a future exit path cannot remember the
 * handle and forget the bytes.
 *
 * ⚠️ IT IS A MODULE FUNCTION TAKING THE CLIENT, so the post's `onSuccess` and
 * `onError` -- which live on the `Mutation` and still run after the hook that
 * started them has unmounted -- clean up a store the NEXT hook instance also
 * reads (C11).
 */
export const forget = (client: QueryClient, localId: string): void => {
  write(client, entries => entries.delete(localId));
};

/**
 * The bytes for one post, read at call time by the mutation function -- and
 * DROPPED as they are handed over (#1069 Story 6.6 code review, decision 2).
 *
 * ⚠️ A TAKE, NOT A GET, AND THAT IS WHAT BOUNDS THE PAYLOAD'S LIFETIME. The
 * store is keyed on the `QueryClient`, which in the app is the module-level
 * singleton that lives until reload, and `forget` is reachable only from the
 * post mutation's `onSuccess`/`onError`. A post that never settles -- a
 * torn-down XHR with no error event, the app backgrounded mid-upload -- would
 * therefore strand a multi-megabyte base64 payload for the whole session, where
 * the `filesRef` this replaced died with the hook. There is exactly one reader
 * (`postMessageMutation`'s `mutationFn`, once, synchronously) and `retry` is
 * `false`, so handing the bytes over and deleting them in the same call makes
 * the payload's lifetime STRUCTURAL rather than dependent on a callback that
 * may never run. The bar, the flag and the handle stay until `forget`.
 *
 * The residual -- an unsettled post's `percent`/`slow`/`abort` -- is bounded per
 * post and measured in bytes, and the liveness gap that stranded it is CLOSED
 * (#1069 Story 6.7): the transport's stall detector settles every post, and
 * the cache subscription in `storeFor` catches the one case where the mutation
 * is removed from under the upload.
 */
export const takeFiles = (
  client: QueryClient,
  localId: string,
): OutgoingMessageFile[] | undefined => {
  const files = storeFor(client).entries.get(localId)?.files;
  if (files) {
    write(client, entries => {
      const current = entries.get(localId);
      if (!current?.files) return false;
      const { files: _taken, ...rest } = current;
      entries.set(localId, rest);
      return true;
    });
  }
  return files;
};

/** Call off one post in flight. Silent when there is no handle yet. */
export const abort = (client: QueryClient, localId: string): void => {
  storeFor(client).entries.get(localId)?.abort?.();
};

/** For `useSyncExternalStore`. Returns the unsubscribe. */
export const subscribe = (
  client: QueryClient,
  listener: () => void,
): (() => void) => {
  const store = storeFor(client);
  store.listeners.add(listener);
  return () => {
    store.listeners.delete(listener);
  };
};

/** For `useSyncExternalStore`. The SAME map instance until a write replaces it. */
export const snapshot = (
  client: QueryClient,
): ReadonlyMap<string, UploadOverlay> => storeFor(client).entries;
